import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../shared/utils.js/axiosConfig";

const initialState = {
  employeePenalties: [],
  currentEmployeePenalty: null,
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  },
  loading: false,
  error: null,
  success: null,
};

// Fetch all employee penalties with pagination and filters
export const fetchEmployeePenalties = createAsyncThunk(
  "employeePenalties/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/employee-penalties", {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch single employee penalty by ID
export const fetchEmployeePenaltyById = createAsyncThunk(
  "employeePenalties/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/employee-penalties/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Create new employee penalty
export const createEmployeePenalty = createAsyncThunk(
  "employeePenalties/create",
  async (penaltyData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/employee-penalties",
        penaltyData,
      );
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Update employee penalty
export const updateEmployeePenalty = createAsyncThunk(
  "employeePenalties/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/employee-penalties/${id}`,
        data,
      );
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Delete employee penalty
export const deleteEmployeePenalty = createAsyncThunk(
  "employeePenalties/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/employee-penalties/${id}`);
      return { id, ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Approve employee penalty
export const approveEmployeePenalty = createAsyncThunk(
  "employeePenalties/approve",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/employee-penalties/${id}/approve`,
      );
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Cancel employee penalty
export const cancelEmployeePenalty = createAsyncThunk(
  "employeePenalties/cancel",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/employee-penalties/${id}/cancel`,
      );
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const employeePenaltiesSlice = createSlice({
  name: "employeePenalties",
  initialState,
  reducers: {
    clearCurrentEmployeePenalty: state => {
      state.currentEmployeePenalty = null;
    },
    clearError: state => {
      state.error = null;
    },
    clearSuccess: state => {
      state.success = null;
    },
    hydrate: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      // Fetch all
      .addCase(fetchEmployeePenalties.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeePenalties.fulfilled, (state, action) => {
        state.loading = false;
        state.employeePenalties = action.payload.data || [];
        state.pagination =
          action.payload.meta ||
          action.payload.pagination ||
          initialState.pagination;
      })
      .addCase(fetchEmployeePenalties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch by ID
      .addCase(fetchEmployeePenaltyById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeePenaltyById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEmployeePenalty = action.payload.data;
      })
      .addCase(fetchEmployeePenaltyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createEmployeePenalty.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmployeePenalty.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          state.employeePenalties.unshift(action.payload.data);
          state.pagination.total += 1;
        }
      })
      .addCase(createEmployeePenalty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateEmployeePenalty.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmployeePenalty.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.employeePenalties.findIndex(
          ep => ep.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.employeePenalties[index] = action.payload.data;
        }
      })
      .addCase(updateEmployeePenalty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteEmployeePenalty.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEmployeePenalty.fulfilled, (state, action) => {
        state.loading = false;
        state.employeePenalties = state.employeePenalties.filter(
          ep => ep.id !== action.payload.id,
        );
        state.pagination.total -= 1;
      })
      .addCase(deleteEmployeePenalty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Approve
      .addCase(approveEmployeePenalty.pending, state => {
        state.loading = true;
      })
      .addCase(approveEmployeePenalty.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.employeePenalties.findIndex(
          ep => ep.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.employeePenalties[index] = action.payload.data;
        }
      })
      .addCase(approveEmployeePenalty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Cancel
      .addCase(cancelEmployeePenalty.pending, state => {
        state.loading = true;
      })
      .addCase(cancelEmployeePenalty.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.employeePenalties.findIndex(
          ep => ep.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.employeePenalties[index] = action.payload.data;
        }
      })
      .addCase(cancelEmployeePenalty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentEmployeePenalty, clearError, clearSuccess } =
  employeePenaltiesSlice.actions;
export default employeePenaltiesSlice.reducer;
